import {
  DeliveryOrderStatusE,
  StatusE,
  UserRoleE,
  VideoContentStatusE,
} from "../../models/enums";
import { isDeliveryOrderPaid } from "./isDeliveryOrderPaid";

/**
 * spec-37 (JMMEDIAT-866) — is the "Remove version" bin offered for this version?
 *
 * Extracted as a pure function for the same reason `canDragAndDropVersion` is: the answer depends on
 * four independent axes (version status, project status, role, order state) and rendering
 * `VideoControlButtons` to check one of them would mean standing up RTK Query, the store, the router
 * and the popup provider — a test of the mocks, not of the rule.
 *
 * 🔴 The interesting clause is the middle one. `WaitingForApprovalOfAdvertisingAgency` used to be a
 * flat exclusion for everybody, and delivery #24134 is what that cost: a delivery-service customer who
 * pressed "Send project", came back without paying and deleted the version was left with no media, no
 * uploader (the backend's `UploadAllowed` is false at status 4) and only a Continue button to the
 * payment page. Spec 37 fixes the backend so the delete ROLLS THE PROJECT BACK to Opened /
 * UploadingFiles, and this opens the control that makes the recovery reachable.
 *
 * Deliberately delivery-service ONLY (D2). In the agency ↔ post-production flow, once post-production
 * approves, the delivery belongs to the ad agency, which recovers a wrong version by cancelling and
 * duplicating the delivery — a path that exists and works. A post-production user therefore still sees
 * no bin at status 4, exactly as today.
 *
 * The paid check is a courtesy, not the guard. `ProjectManager.DeleteUploadedFile` refuses a paid
 * order server-side and trusts `PaymentVerifiedAt`, which no view model exposes; this only keeps a
 * customer from clicking a button that would fail. Never treat it as the enforcement point.
 *
 * ⚠️ `DeliveryOrderStatusE.Created = 0` is FALSY. Do not "simplify" the `orderStatus` handling with a
 * `!orderStatus` test — a real order sitting at Created is indistinguishable from "no order" that way.
 * `isDeliveryOrderPaid` answers the actual question and treats both as not paid.
 */
export const canRemoveVersion = (
  versionStatus?: VideoContentStatusE | string,
  projectStatus?: StatusE,
  role?: UserRoleE,
  orderStatus?: DeliveryOrderStatusE | null
): boolean => {
  const versionIsRemovable =
    versionStatus === VideoContentStatusE.UPLOADED ||
    versionStatus === VideoContentStatusE.ENCODED ||
    versionStatus === VideoContentStatusE.LOW_RES_ONLY ||
    versionStatus === VideoContentStatusE.ENCODING_ERROR ||
    versionStatus === VideoContentStatusE.UPLOADED_ERROR;

  if (!versionIsRemovable) {
    return false;
  }

  // Once the delivery has left for the broadcasters it is out of everyone's hands — unchanged, and
  // unchanged for delivery service too.
  if (
    projectStatus === StatusE.ApprovedAndSentToBroadcaster ||
    projectStatus === StatusE.DownloadedByBroadcaster ||
    projectStatus === StatusE.DownloadedByAllBroadcasters
  ) {
    return false;
  }

  if (projectStatus === StatusE.WaitingForApprovalOfAdvertisingAgency) {
    return role === UserRoleE.DeliveryService && !isDeliveryOrderPaid(orderStatus);
  }

  return true;
};

export default canRemoveVersion;
